/**
 * FlexChildField - Controls how an element behaves as a flex child (responsive)
 *
 * Provides a mode selector (Auto, Fill, Hug, Custom) and a percentage
 * input when Custom mode is selected. Supports per-breakpoint overrides
 * (base/tablet/desktop) following the same responsive pattern as other fields.
 *
 * CSS flex mappings:
 * - Auto:    (no override — default flex behavior)
 * - Fill:    flex: 1 1 0%     (grow to fill available space)
 * - Hug:    flex: 0 0 auto   (size to content, don't grow or shrink)
 * - Custom:  flex: 0 0 <X>%   (use a percentage-based flex-basis)
 *
 * Value format (responsive):
 *   { base: { mode, basis }, tablet: { mode, basis } | null, desktop: { mode, basis } | null }
 *
 * Legacy format (non-responsive):
 *   { mode, basis }  — auto-upgraded to responsive on first edit
 */

import React, { useState } from 'react';
import {
  Maximize2, Minimize2, Move, SlidersHorizontal,
  Smartphone, Tablet, Monitor,
} from 'lucide-react';

const FLEX_CHILD_MODES = [
  { value: 'auto', label: 'Auto', icon: Move, description: 'Default flex behavior' },
  { value: 'fill', label: 'Fill', icon: Maximize2, description: 'Grow to fill space' },
  { value: 'hug', label: 'Hug', icon: Minimize2, description: 'Fit to content' },
  { value: 'custom', label: 'Custom', icon: SlidersHorizontal, description: 'Set a % width' },
];

const BREAKPOINTS = [
  { id: 'base', label: 'Mobile', icon: Smartphone },
  { id: 'tablet', label: 'Tablet', icon: Tablet },
  { id: 'desktop', label: 'Desktop', icon: Monitor },
];

/**
 * FlexChildField Component
 *
 * @param {Object} props
 * @param {Object} props.field - Field configuration
 * @param {Object} props.value - Responsive value { base, tablet, desktop }
 * @param {Function} props.onChange - Callback when value changes
 * @param {boolean} [props.compact] - Use compact layout
 * @param {string} [props.activeBreakpoint] - External breakpoint control
 * @param {Function} [props.onBreakpointChange] - Callback when breakpoint changes
 */
export default function FlexChildField({
  field, value, onChange, compact = false,
  activeBreakpoint: externalBreakpoint,
  onBreakpointChange,
}) {
  const [localBreakpoint, setLocalBreakpoint] = useState('base');
  const activeBreakpoint = externalBreakpoint ?? localBreakpoint;

  const handleBreakpointChange = (bp) => {
    if (onBreakpointChange) {
      onBreakpointChange(bp);
    } else {
      setLocalBreakpoint(bp);
    }
  };

  // Normalize to responsive format
  const responsiveObj = normalizeResponsiveValue(value);

  // Get the effective value for the current breakpoint (cascade: desktop → tablet → base)
  const currentValue = responsiveObj[activeBreakpoint] || responsiveObj.base || { mode: 'auto', basis: 50 };
  const { mode, basis } = currentValue;

  // Whether current breakpoint has its own override
  const isInheriting = activeBreakpoint !== 'base' && !responsiveObj[activeBreakpoint];

  const handleModeChange = (newMode) => {
    const newVal = { mode: newMode, basis: currentValue.basis };
    onChange({ ...responsiveObj, [activeBreakpoint]: newVal });
  };

  const handleBasisChange = (e) => {
    const newBasis = Math.min(100, Math.max(1, parseInt(e.target.value, 10) || 50));
    onChange({ ...responsiveObj, [activeBreakpoint]: { mode: 'custom', basis: newBasis } });
  };

  const clearOverride = () => {
    if (activeBreakpoint !== 'base') {
      onChange({ ...responsiveObj, [activeBreakpoint]: null });
    }
  };

  const content = (
    <div className="space-y-1.5">
      {/* Breakpoint pills */}
      <div className="flex items-center gap-0.5 bg-gray-100 rounded p-0.5">
        {BREAKPOINTS.map((bp) => {
          const Icon = bp.icon;
          const hasOverride = bp.id !== 'base' && responsiveObj[bp.id];
          return (
            <button
              key={bp.id}
              type="button"
              onClick={() => handleBreakpointChange(bp.id)}
              title={bp.label}
              className={`relative p-1 rounded transition-colors
                ${activeBreakpoint === bp.id
                  ? 'bg-white shadow-sm text-gray-900'
                  : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              <Icon className="w-3 h-3" />
              {hasOverride && (
                <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-blue-500 rounded-full" />
              )}
            </button>
          );
        })}
      </div>

      {/* Mode selector buttons */}
      <div className="flex items-center gap-0.5 bg-gray-100 rounded p-0.5">
        {FLEX_CHILD_MODES.map((option) => {
          const Icon = option.icon;
          const isActive = mode === option.value;

          return (
            <button
              key={option.value}
              type="button"
              onClick={() => handleModeChange(option.value)}
              className={`
                flex-1 px-1 py-1 rounded transition-colors flex items-center justify-center gap-1 h-6
                ${isActive
                  ? 'bg-white shadow-sm text-gray-900'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }
              `}
              title={option.description}
            >
              <Icon className="w-3 h-3" />
              <span className="text-[10px] leading-none font-medium">{option.label}</span>
            </button>
          );
        })}
      </div>

      {/* Custom basis % input — only shown when custom mode is active */}
      {mode === 'custom' && (
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-500 w-16 flex-shrink-0">Basis</label>
          <div className="flex-1 flex items-center gap-2">
            <input
              type="range"
              min={1}
              max={100}
              value={basis}
              onChange={handleBasisChange}
              className="flex-1 h-1 accent-blue-500"
            />
            <div className="flex items-center gap-0.5">
              <input
                type="number"
                min={1}
                max={100}
                value={basis}
                onChange={handleBasisChange}
                className="w-12 px-1.5 py-0.5 text-xs border border-gray-200 rounded bg-gray-50 text-center
                  focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
              />
              <span className="text-xs text-gray-400">%</span>
            </div>
          </div>
        </div>
      )}

      {/* Inheritance / clear override */}
      {activeBreakpoint !== 'base' && isInheriting && (
        <div className="text-[10px] text-gray-400 bg-blue-50 px-2 py-0.5 rounded">
          Inheriting from Mobile
        </div>
      )}
      {activeBreakpoint !== 'base' && !isInheriting && (
        <button
          onClick={clearOverride}
          className="w-full text-[10px] text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-100 px-2 py-0.5 rounded transition-colors"
        >
          Clear override (inherit)
        </button>
      )}
    </div>
  );

  // Compact mode: show label + controls
  if (compact) {
    return (
      <div className="py-0.5">
        <label className="text-xs text-gray-500 mb-1 block">
          {field.label}
        </label>
        {content}
      </div>
    );
  }

  // Standard mode
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">
        {field.label}
      </label>
      {content}
      {field.description && (
        <p className="mt-1 text-xs text-gray-500">{field.description}</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Utility functions (exported for use in renderers)
// ---------------------------------------------------------------------------

/**
 * Normalize a flexChild value to the responsive format.
 * Handles both legacy { mode, basis } and new { base, tablet, desktop }.
 */
export function normalizeResponsiveValue(value) {
  if (!value || typeof value !== 'object') {
    return { base: { mode: 'auto', basis: 50 }, tablet: null, desktop: null };
  }
  // Already responsive
  if (value.base !== undefined) {
    return {
      base: normalizeSingleValue(value.base),
      tablet: value.tablet ? normalizeSingleValue(value.tablet) : null,
      desktop: value.desktop ? normalizeSingleValue(value.desktop) : null,
    };
  }
  // Legacy flat format { mode, basis }
  if (value.mode !== undefined) {
    return { base: normalizeSingleValue(value), tablet: null, desktop: null };
  }
  return { base: { mode: 'auto', basis: 50 }, tablet: null, desktop: null };
}

function normalizeSingleValue(v) {
  if (!v || typeof v !== 'object') return { mode: 'auto', basis: 50 };
  return {
    mode: v.mode || 'auto',
    basis: typeof v.basis === 'number' ? v.basis : 50,
  };
}

/**
 * Resolve the effective flexChild value for a given breakpoint using cascade:
 *   desktop → tablet → base
 *
 * @param {Object} flexChildValue - Responsive or legacy flexChild value
 * @param {string} breakpoint - 'base' | 'tablet' | 'desktop'
 * @returns {{ mode: string, basis: number }}
 */
export function resolveFlexChildForBreakpoint(flexChildValue, breakpoint = 'base') {
  const resp = normalizeResponsiveValue(flexChildValue);

  if (breakpoint === 'desktop') {
    return resp.desktop ?? resp.tablet ?? resp.base;
  }
  if (breakpoint === 'tablet') {
    return resp.tablet ?? resp.base;
  }
  return resp.base;
}

/**
 * Convert a single flexChild value ({ mode, basis }) to CSS flex properties.
 *
 * @param {{ mode: string, basis: number }} singleValue
 * @returns {Object} CSS properties (e.g. { flex: '1 1 0%' })
 */
function singleValueToStyles(singleValue) {
  const { mode, basis } = normalizeSingleValue(singleValue);

  switch (mode) {
    case 'fill':
      return { flex: '1 1 0%' };
    case 'hug':
      return { flex: '0 0 auto' };
    case 'custom':
      return { flex: `0 0 ${basis}%` };
    case 'auto':
    default:
      return {}; // No override — use default flex behavior
  }
}

/**
 * Convert a flexChild value to CSS flex properties for a given breakpoint.
 * Exported for use in renderers.
 *
 * @param {Object} flexChildValue - Responsive or legacy flexChild value
 * @param {string} [breakpoint='base'] - Current breakpoint
 * @returns {Object} CSS properties { flex }
 */
export function getFlexChildStyles(flexChildValue, breakpoint = 'base') {
  const resolved = resolveFlexChildForBreakpoint(flexChildValue, breakpoint);
  return singleValueToStyles(resolved);
}
